import { Box, Button, Stack, Typography } from '@mui/material';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { useTranslator } from '@/hooks/useTranslator';
import logRocketPromise, { LogRocketInstance } from '@/services/logRocket';

interface InnerProps {
  readonly title: string;
  readonly description: string;
  readonly tryAgainLabel: string;
  readonly refreshLabel: string;
}

interface State {
  readonly error?: Error;
}

class AppErrorBoundaryInner extends React.Component<React.PropsWithChildren<InnerProps>, State> {
  constructor(props: React.PropsWithChildren<InnerProps>) {
    super(props);
    this.state = { error: undefined };
    this.handleTryAgain = this.handleTryAgain.bind(this);
    this.handleRefresh = this.handleRefresh.bind(this);
  }

  static getDerivedStateFromError(error: Error): State {
    return { error: error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('AppErrorBoundary caught an error:', error, errorInfo);
    void logRocketPromise.then((logRocket: LogRocketInstance): void => {
      logRocket.captureException(error, {
        extra: {
          componentStack: errorInfo.componentStack ?? '',
          source: 'react_error_boundary',
        },
      });
    });
  }

  handleTryAgain(): void {
    this.setState({ error: undefined });
  }

  handleRefresh(): void {
    window.location.reload();
  }

  render(): React.ReactNode {
    const { children, title, description, tryAgainLabel, refreshLabel } = this.props;
    const { error } = this.state;

    if (error) {
      return (
        <Box sx={styles.container}>
          <Typography variant="h5" sx={styles.title}>
            {title}
          </Typography>
          <Typography variant="body1" sx={styles.description}>
            {description}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={this.handleTryAgain}>
              {tryAgainLabel}
            </Button>
            <Button variant="outlined" onClick={this.handleRefresh}>
              {refreshLabel}
            </Button>
          </Stack>
        </Box>
      );
    }

    return children;
  }
}

export const AppErrorBoundary: React.FC<React.PropsWithChildren> = ({
  children,
}: React.PropsWithChildren): React.ReactElement => {
  const tr = useTranslator();

  return (
    <AppErrorBoundaryInner
      title={tr('APP_ERROR_BOUNDARY_TITLE')}
      description={tr('APP_ERROR_BOUNDARY_DESCRIPTION')}
      tryAgainLabel={tr('APP_ERROR_BOUNDARY_TRY_AGAIN')}
      refreshLabel={tr('APP_ERROR_BOUNDARY_REFRESH')}>
      {children}
    </AppErrorBoundaryInner>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    justifyContent: 'center',
    minHeight: '100vh',
    padding: 4,
    textAlign: 'center',
  },
  description: {
    color: 'text.secondary',
    maxWidth: 480,
  },
  title: {
    fontWeight: 600,
  },
};
